import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router";
import { EllipsisText } from "../../../modules/generic/components/ellipsis-text";
import { reviewStatusToPill, statusToPill } from "../../../modules/moment-card/utils";
import { LiveSearchField } from "../../../modules/search/live-search-field";
import { formatDate, fromISO8601 } from "../../../utils/iso8601";
import { statusFilterOptions, useReviews } from "../hooks/use-reviews";

import type { ReviewsVariant } from "../hooks/use-reviews";
import type { SelectChangeEvent } from "@mui/material";
import type { FC } from "react";

const emptyState: Record<ReviewsVariant, { title: string; subtitle: string }> = {
  "in-progress": {
    title: "There’s nothing to approve right now",
    subtitle:
      "You’ll receive an email notification when someone asks you to approve a Communication",
  },
  previous: {
    title: "Completed approvals will appear here",
    subtitle:
      "Once a Communication you have been asked to approve is published, it will appear here.",
  },
};

const ReviewsEmptyState: FC<{ variant: ReviewsVariant }> = ({ variant }) => (
  <Paper>
    <Stack spacing={1} alignItems="center" textAlign="center" sx={{ px: 3, py: 6 }}>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        {emptyState[variant].title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {emptyState[variant].subtitle}
      </Typography>
    </Stack>
  </Paper>
);

export const ReviewsTable: FC<{ variant: ReviewsVariant }> = ({ variant }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "";
  const { reviews, loading, error } = useReviews(variant);

  const handleStatusChange = (event: SelectChangeEvent): void => {
    const params = new URLSearchParams(searchParams);
    if (event.target.value) params.set("status", event.target.value);
    else params.delete("status");
    setSearchParams(params);
  };

  if (!loading && !error && reviews.length === 0) return <ReviewsEmptyState variant={variant} />;

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="reviews-status-filter-label">Status</InputLabel>
          <Select
            labelId="reviews-status-filter-label"
            label="Status"
            value={status}
            onChange={handleStatusChange}
          >
            <MenuItem value="">All</MenuItem>
            {statusFilterOptions[variant].map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ minWidth: 280, maxWidth: 400 }}>
          <LiveSearchField label="Search approvals" size="small" />
        </Box>
      </Stack>

      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">Unable to load approvals</Alert>}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Communication</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map(
              ({ moment_id, title, momentStatus, reviewStatus, creator, requestedAt, message }) => (
                <TableRow key={moment_id}>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/moments/${moment_id}/review`}
                      data-analytics-id="reviews-communication-link"
                      underline="hover"
                    >
                      {title}
                    </Link>
                  </TableCell>
                  <TableCell>{creator}</TableCell>
                  <TableCell>
                    {/* If we are displaying previous-review we display moment status,
                    if we are displaying in-progress then show the review status  */}
                    {variant === "previous"
                      ? statusToPill(momentStatus)
                      : reviewStatusToPill(reviewStatus, "soft")}
                  </TableCell>
                  <TableCell>{formatDate(fromISO8601(requestedAt))}</TableCell>
                  <TableCell>
                    {message && <EllipsisText text={message} lines={2} variant="body2" />}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
